import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getChainModeActivationJobsApi,
  retryChainModeActivationJobApi,
  startChainModeActivationJobApi,
} from "../api/chainModeActivationJobsApi";
import type { ChainId, ChainModeActivationJobId, StoreId } from "@KassaroAB/shared/types/ids";
import { isActiveJobStatus } from "@KassaroAB/shared/utils/jobStatus";

const jobsQueryKey = (chainId: ChainId | undefined) =>
  ["chains", chainId, "chainModeActivationJobs"] as const;

export const useGetChainModeActivationJobsQuery = (
  chainId: ChainId | undefined,
) => {
  return useQuery({
    queryKey: jobsQueryKey(chainId),
    queryFn: () => getChainModeActivationJobsApi(chainId!),
    enabled: !!chainId,
    refetchInterval: (query) => {
      const latestJob = query.state.data?.[0];
      if (latestJob && isActiveJobStatus(latestJob.status)) {
        return 2000;
      }
      return false;
    },
  });
};

export const useStartChainModeActivationJobMutation = (
  chainId: ChainId | undefined,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storeId?: StoreId) =>
      startChainModeActivationJobApi(chainId!, storeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsQueryKey(chainId) });
      queryClient.invalidateQueries({ queryKey: ["chains"] });
    },
  });
};

export const useRetryChainModeActivationJobMutation = (
  chainId: ChainId | undefined,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chainModeActivationJobId: ChainModeActivationJobId) =>
      retryChainModeActivationJobApi(chainId!, chainModeActivationJobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsQueryKey(chainId) });
      queryClient.invalidateQueries({ queryKey: ["chains"] });
    },
  });
};
